/* eslint-disable no-empty-pattern */
import SearchItemMapper from 'legacy/lut/classes/mappers/SearchItemMapper'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { CrudStoreItemScout } from 'legacy/lut/classes/models/CrudStoreItemScout'
import { TSearchItem } from 'legacy/lut/classes/models/SearchItem'
import { SearchMetric } from 'legacy/lut/configs/metrics'
import { appGetState } from 'legacy/lut/store'

import { Market, Region } from 'clickNgo/configs/geo'
import { IUser } from 'core-components/sitebuilder/models/IUser'
import { IDataProviderFilter, IDataProviderSort, Logic, Operator } from 'core-components/types'
import { toServerFormat } from 'core/classes/helpers/DateHelper'
import { arrayUnique, compose } from 'core/classes/helpers/Helper'
import { getTokens } from 'core/classes/helpers/StorageHelper'
import { proxy } from 'core/classes/helpers/UrlHelper'
import { HttpCancel, HttpHeaders, Rest } from 'core/classes/http'
import { MarketApi } from 'core/classes/services/api/MarketApi'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'
import { parseSearchIds } from 'shared/helpers/strings'
import { measureRequest } from 'shared/lib/metrics'
import settings from 'src/config/settings'

export const MAX_SEARCH_VALUE_LENGTH = 200

export const sanitizeSearchValue = (
  value: string = '',
  maxLength: number = MAX_SEARCH_VALUE_LENGTH
) => value.trim().slice(0, maxLength)

const escapeSearchFilterValue = (value: string) => value.replace(/'/g, "''")

// Legacy v3 search procedures require apostrophes in filter values to be doubled.
const serializeSearchFilter = (filter: IDataProviderFilter): string =>
  JSON.stringify(filter, (key, value) => {
    if (key !== 'value') {
      return value
    }

    if (typeof value === 'string') {
      return escapeSearchFilterValue(value)
    }

    if (Array.isArray(value)) {
      return value.map((item) => (typeof item === 'string' ? escapeSearchFilterValue(item) : item))
    }

    return value
  })

export enum SearchRequestType {
  Partner = 'partner',
  Contact = 'contact',
}

export interface IGroupedSearchItem {
  country: string
  items: TSearchItem[]
}
export interface ISearchResult {
  items: TSearchItem[]
  total: number
  success?: boolean
}

interface IDownloadSearchExcelParams {
  formData: TSearchParams
  type: SearchRequestType
  cancelObject: HttpCancel
}

export interface TSearchParams {
  query: string
  AgreementNo: string
  PartnerID: string
  CommunicationName: string
  CompanyLegalName: string
  TaxID: string
  Country: string
  City: string
  State: string
  ZIPCode: string
  LocationID: string
  STORE_NUM: string
  URL: string
  Address: string
  ContactID: string
  LearnerID: string
  FirstName: string
  LastName: string
  Email: string
  IsBusinessPartner: string
  HierarchyType: string
  ISR: string
  IsPFPartner: string
  IsAmpRet: string
  IsAmpComm: string
  IsRet: string
  IsComm: string
  IsLegal: string
  GeoMarket: string
  IsDis: string
}

export class SearchApi {
  public static IN_REGION = 'In Region'
  private timer: any
  private isSuperAdmin
  private userMarkets: string[]
  private region
  private isExceptChinaMarket
  private user: IUser

  constructor(user: IUser) {
    this.user = user
    this.isSuperAdmin = user.roleIds.includes('SuperAdmin')
    this.userMarkets = user.geoMarkets
    this.region = settings.region ? MarketApi.getRegion(user.geoMarket).Region : 'EMEA'
    this.isExceptChinaMarket =
      !this.userMarkets.includes(Market.GREATER_CHINA) && !this.isSuperAdmin
  }

  private getPathByType = (type: SearchRequestType) =>
    type === SearchRequestType.Partner
      ? `/webservice/197/sp/v3/spSCOUTSearchRegion/call`
      : `/webservice/197/sp/v3/spSCOUTSearchContactsRegion/call`

  /**
   * @param query
   * @param baseFilter
   * @param type
   * @param cancelObject
   */
  public quickSearch = async (
    query: string,
    baseFilter: IDataProviderFilter,
    type: SearchRequestType,
    cancelObject?: HttpCancel
  ): Promise<ISearchResult> => {
    if (!query) {
      return { items: [], total: 0 }
    }
    const url = proxy(`${this.getPathByType(type)}`)
    const { app } = appGetState()
    const [accessToken] = await getTokens(app.ownerId)
    const headers = new HttpHeaders()
    if (accessToken) {
      headers.add('Authorization', `Bearer ${accessToken}`)
    }

    const payload = {
      'p.Filter': serializeSearchFilter(baseFilter),
      'p.IsQuickSearch': 1,
      take: 10,
    }

    payload['p.Region'] = this.region
    const response = await Rest.post({ url, payload, cancelObject, headers })

    if (cancelObject && cancelObject.canceled) {
      return { items: [], total: 0 }
    }

    const items = this.map(response['Rows'], [query.trim()])
    return { items, total: response['Total'] }
  }

  /**
   * @param query
   * @param cb
   * @param ts
   * @param type
   * @param cancelObj
   */
  public debouncedSearch = (
    query: string,
    cb: (result: ISearchResult, ts: number) => void,
    ts: number,
    type: SearchRequestType,
    cancelObj: HttpCancel
  ) => {
    query = sanitizeSearchValue(query)
    if (this.timer) {
      clearTimeout(this.timer)
    }
    this.timer = setTimeout(async () => {
      const cancelObject = new HttpCancel()
      cancelObj.setCancelFunction(compose(cancelObject.cancel))

      let result: any = {}
      if (type === SearchRequestType.Partner) {
        const filter = this.createFilter(query, {}, type)
        result = await measureRequest(SearchMetric.QuickPartners, this.user, () =>
          this.quickSearch(query, filter, type, cancelObject)
        ).catch(Logger.error)
      } else {
        const filter = this.createFilter(query, {}, type)
        const normalizePhone = query.trim().replace(/\D+/g, '')
        filter.filters.push({
          field: 'LastName',
          operator: Operator.CONTAINS,
          value: query.trim(),
        })
        if (normalizePhone && normalizePhone === query.trim()) {
          const fields = ['MainPhone', 'WorkPhone', 'MobilePhone']
          fields.forEach((item: string) => {
            filter.filters.push({
              field: item,
              operator: Operator.CONTAINS,
              value: normalizePhone,
            })
          })
        }
        result = await measureRequest(SearchMetric.QuickContacts, this.user, () =>
          this.quickSearch(query, filter, type, cancelObject)
        ).catch(Logger.error)
      }

      cb(result as any, ts)
    }, 500)
  }

  public static getFilterForSearch = (
    partnerIds: string[],
    searchCode?: string,
    searchFields?: string[],
    type?: string
  ) => {
    const filterPids: IDataProviderFilter = {
      logic: Logic.OR,
      filters: partnerIds.map((i) => ({
        field: 'PARTNER_ID',
        operator: Operator.EQUAL,
        value: i,
      })),
    }
    const filter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [filterPids],
    }
    if (searchCode && searchFields) {
      const searchFilters: any = []
      searchFields.forEach((i) => {
        searchFilters.push({
          field: i,
          operator: Operator.CONTAINS,
          value: searchCode,
        })
      })
      filter.filters[filter.filters.length] = {
        logic: Logic.OR,
        filters: searchFilters,
      }
    }
    if (type) {
      filter.filters[filter.filters.length] = {
        field: 'CodeType',
        operator: Operator.EQUAL,
        value: type,
      }
    }

    return filter
  }

  public static fetchProgramCodes = async (
    partnerIds: string[],
    page?: number,
    pageSize?: number,
    searchItems?: string,
    type?: string,
    filter?: any
  ) => {
    const pIds: string[] = arrayUnique(partnerIds)
    if (pIds.length) {
      const crud = CrudStore.get(CrudStoreItemScout.WW_PartnerFlags_DateFormat, 'fetchProgramCodes')

      const combinedFilter = this.getFilterForSearch(pIds, searchItems, ['Code', 'CodeName'], type)
      if (filter) combinedFilter.filters.push(filter)

      const endDate = new Date()
      endDate.setMinutes(0)
      endDate.setSeconds(0)
      endDate.setMilliseconds(0)

      const filterDate = {
        field: 'END_DATE',
        operator: Operator.GTE,
        value: toServerFormat(endDate),
      }

      combinedFilter.filters.push(filterDate)

      crud.setFilter(combinedFilter)
      crud.setSort({ field: 'Code', dir: 'asc' })
      crud.setPaging({
        page: page || 0,
        pageSize: pageSize || 100,
      })
      const response = await crud.fetch()
      return response
    }
    return {
      items: [],
      total: 0,
    }
  }

  public autocompleteSearch = async (
    type: string,
    data: string,
    cancelObject: HttpCancel,
    size?: number,
    asIs?: boolean,
    market?: string,
    showBranches?: boolean
  ) => {
    let rows: any[] = []
    const result: any[] = []
    const filter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [
        {
          field: type,
          operator: Operator.CONTAINS,
          value: data,
        },
        {
          field: 'Region',
          operator: Operator.EQUAL,
          value: this.region,
        },
      ],
    }

    if (settings.region && market) {
      filter.filters.push({
        field: 'Market',
        operator: Operator.EQUAL,
        value: market,
      })
    }

    let crud: any
    let newCrud
    switch (type) {
      case 'PartnerID':
      case 'CommunicationName':
      case 'CompanyLegalName':
        newCrud = CrudStore.get(CrudStoreItem.PartnerAllData, 'autocompleteSearchWithRegion')
        if (!showBranches) {
          filter.filters.push({
            field: 'HierarchyType',
            operator: Operator.EQUAL,
            value: 'Headquarters',
          })
        }
        break
      case 'Email':
      case 'ContactID':
      case 'LastName':
        crud = CrudStore.get(CrudStoreItem.ContactAllData, 'autocompleteSearchWithRegion')
        break
    }
    const sort: IDataProviderSort = { field: type, dir: 'asc' }
    if (crud) {
      crud.setFilter(filter)
      crud.setSort(sort)
      crud.setPaging({
        page: 0,
        pageSize: size || 5,
      })
      rows = (await crud.fetch()).items
    } else if (newCrud) {
      newCrud.setFilter(filter)
      newCrud.setSort(sort)
      newCrud.setPaging({
        page: 0,
        pageSize: size || 5,
      })
      rows = (await newCrud.fetch()).items
    }

    if (asIs) {
      return rows
    }

    rows.forEach((item: any) => {
      result.push({ key: item[type], label: item[type] })
    })
    return result
  }

  /**
   * @param formData
   * @param type
   * @param page
   * @param pageSize
   * @param cancelObject
   */

  public advancedSearch = async (
    formData: TSearchParams,
    type: SearchRequestType,
    page: number,
    pageSize: number,
    cancelObject?: HttpCancel
  ): Promise<ISearchResult> => {
    const url = proxy(`${this.getPathByType(type)}`)
    const { app } = appGetState()
    const [accessToken] = await getTokens(app.ownerId)
    const headers = new HttpHeaders()
    if (accessToken) {
      headers.add('Authorization', `Bearer ${accessToken}`)
    }

    const filter = this.createAdvancedFilter(formData, type)

    const payload = {
      'p.Filter': serializeSearchFilter(filter),
      take: pageSize,
      skip: page * pageSize,
    }

    if (
      formData['ISR'] &&
      appGetState().user.roleIds &&
      (appGetState().user.roleIds.indexOf('PBM') > -1 ||
        appGetState().user.roleIds.indexOf('AOM') > -1)
    ) {
      payload[`p.IsSalesTeamPartner`] = formData['ISR']
    }

    payload['p.Region'] = this.region
    if (formData.IsPFPartner === '1') {
      payload[`p.IsBPPOnly`] = 1
    }
    if (type === SearchRequestType.Partner && formData.IsDis === '1') {
      payload[`p.IsDis`] = 1
    }
    const response = await Rest.post({ url, payload, cancelObject, headers })

    if (cancelObject && cancelObject.canceled) {
      return { items: [], total: 0 }
    }

    const highlightWords: string[] = []

    const {
      IsPFPartner: {},
      IsDis: {},
      IsBusinessPartner: {},
      HierarchyType: {},
      ...formDataCopy
    } = formData

    Object.keys(formDataCopy).forEach((field) => {
      if (formDataCopy[field]) {
        highlightWords.push(formDataCopy[field])
      }
    })
    if (formDataCopy['query']) {
      highlightWords.push(formDataCopy['query'])
    }

    if (response) {
      const items = this.map(response['Rows'], highlightWords)
      return { items, total: items.length }
    }

    return { items: [], total: 0, success: false }
  }

  public downloadSearchExcel = async ({
    formData,
    type,
    cancelObject,
  }: IDownloadSearchExcelParams) => {
    const { app } = appGetState()
    const [accessToken] = await getTokens(app.ownerId)
    const headers = new HttpHeaders()
    if (accessToken) {
      headers.add('Authorization', `Bearer ${accessToken}`)
    }

    const url = proxy(`${this.getPathByType(type)}/export`)

    const filter = this.createAdvancedFilter(formData, type)

    if (
      formData.GeoMarket === SearchApi.IN_REGION &&
      this.region === Region.APJ &&
      this.isExceptChinaMarket
    ) {
      filter.filters.push({
        field: 'GeoMarket',
        operator: Operator.NOTEQUAL,
        value: Market.GREATER_CHINA,
      })
    }

    if (settings.region && formData.GeoMarket && formData.GeoMarket !== SearchApi.IN_REGION) {
      filter.filters.push({
        field: 'GeoMarket',
        operator: Operator.EQUAL,
        value: formData.GeoMarket,
      })
    }

    const payload = {
      'p.Filter': serializeSearchFilter(filter),
      take: 10,
    }

    if (
      formData['ISR'] &&
      appGetState().user.roleIds &&
      (appGetState().user.roleIds.indexOf('PBM') > -1 ||
        appGetState().user.roleIds.indexOf('AOM') > -1)
    ) {
      payload[`p.IsSalesTeamPartner`] = formData['ISR']
    }
    if (type === SearchRequestType.Partner && formData.IsDis === '1') {
      payload[`p.IsDis`] = 1
    }
    if (type === SearchRequestType.Partner && formData.IsAmpRet) {
      payload[`p.IsAmpRet`] = 1
    }
    if (type === SearchRequestType.Partner && formData.IsAmpComm) {
      payload[`p.IsAmpComm`] = 1
    }
    if (type === SearchRequestType.Partner && formData.IsRet) {
      payload[`p.IsRet`] = 1
    }
    if (type === SearchRequestType.Partner && formData.IsComm) {
      payload[`p.IsComm`] = 1
    }

    payload['p.Region'] = this.region
    if (formData.IsPFPartner === '1') {
      payload[`p.IsBPPOnly`] = 1
    }
    return await Rest.post({ url, payload, cancelObject, headers })
  }

  private map(rows: any[], highlightWords: string[]): TSearchItem[] {
    return SearchItemMapper.map(rows, highlightWords)
  }

  /**
   * @param query
   * @param switches
   * @param tableId
   */
  private createFilter(query: string, switches: any, type: SearchRequestType): IDataProviderFilter {
    const partnerIds = parseSearchIds(query)
    query = sanitizeSearchValue(query)
    const filter: IDataProviderFilter = {
      logic: Logic.OR,
      filters: [],
    }

    filter.filters[filter.filters.length] =
      partnerIds.length > 1
        ? {
            logic: Logic.OR,
            filters: partnerIds.map((id) => ({
              field: 'PartnerID',
              operator: Operator.CONTAINS,
              value: id,
            })),
          }
        : {
            field: 'PartnerID',
            operator: Operator.CONTAINS,
            value: partnerIds[0] ?? '',
          }

    const fieldsCon = ['Email', 'CommunicationName', 'TaxID']
    fieldsCon.forEach((field) => {
      filter.filters[filter.filters.length] = {
        field,
        operator: Operator.CONTAINS,
        value: query.trim(),
      }
    })

    if (type === SearchRequestType.Partner) {
      const fields = ['LocationID']
      fields.forEach((field) => {
        filter.filters[filter.filters.length] = {
          field,
          operator: Operator.EQUAL,
          value: query.trim(),
        }
      })
      if (!query.replace(/\D+/g, '')) {
        filter.filters[filter.filters.length] = {
          field: 'Country',
          operator: Operator.EQUAL,
          value: query.trim(),
        }
      }
      filter.filters[filter.filters.length] = {
        field: 'URL',
        operator: Operator.CONTAINS,
        value: query.trim(),
      }
    }

    if (switches.IsBusinessPartner) {
      filter.filters[filter.filters.length] = {
        field: 'IsBusinessPartner',
        operator: Operator.CONTAINS,
        value: switches.BusinessPartner,
      }
    }

    if (switches.ISR) {
      filter.filters[filter.filters.length] = {
        field: 'ISR',
        operator: Operator.CONTAINS,
        value: switches.Mine,
      }
    }

    if (type === SearchRequestType.Contact) {
      const fields = ['ContactID', 'LearnerID']
      fields.forEach((field) => {
        filter.filters[filter.filters.length] = {
          field,
          operator: Operator.EQUAL,
          value: query.trim(),
        }
      })
    }
    if (switches.IsLegal === 'legal' && type === SearchRequestType.Partner) {
      const fields = [
        'LegalCity',
        'LegalCountry',
        'LegalPostalCode',
        'LegalAddr1',
        'LegalAddr2',
        'LegalAddr3',
      ]
      fields.forEach((field) => {
        filter.filters[filter.filters.length] = {
          field,
          operator: Operator.CONTAINS,
          value: query.trim(),
        }
      })
    } else {
      const fields = ['ZIPCode', 'AddressLine1', 'AddressLine2', 'AddressLine3']
      if (!query.replace(/\D+/g, '')) {
        filter.filters[filter.filters.length] = {
          field: 'City',
          operator: Operator.CONTAINS,
          value: query.trim(),
        }
      }
      fields.forEach((field) => {
        filter.filters[filter.filters.length] = {
          field,
          operator: Operator.CONTAINS,
          value: query.trim(),
        }
      })
    }
    if (type === SearchRequestType.Partner) {
      if (switches.HierarchyType) {
        filter.filters[filter.filters.length] = {
          field: 'HierarchyType',
          operator: Operator.CONTAINS,
          value: switches.HierarchyType || '',
        }
      }
    }
    return filter
  }

  public createAdvancedFilter(
    formData: TSearchParams,
    type: SearchRequestType
  ): IDataProviderFilter {
    const partnerIds = parseSearchIds(formData.PartnerID)
    const sanitizedFormData = { ...formData } as Record<string, unknown>
    Object.keys(sanitizedFormData).forEach((key) => {
      const value = sanitizedFormData[key]
      if (typeof value === 'string') {
        sanitizedFormData[key] = sanitizeSearchValue(value)
      }
    })
    formData = sanitizedFormData as unknown as TSearchParams
    const filter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [],
    }
    const AddressFilter: IDataProviderFilter = {
      logic: Logic.OR,
      filters: [],
    }

    if (
      formData.GeoMarket === SearchApi.IN_REGION &&
      this.region === Region.APJ &&
      this.isExceptChinaMarket
    ) {
      filter.filters.push({
        field: 'GeoMarket',
        operator: Operator.NOTEQUAL,
        value: Market.GREATER_CHINA,
      })
    }

    if (settings.region && formData.GeoMarket && formData.GeoMarket !== SearchApi.IN_REGION) {
      filter.filters.push({
        field: 'GeoMarket',
        operator: Operator.EQUAL,
        value: formData.GeoMarket,
      })
    }

    const { query, IsLegal, ...searchParams } = formData
    const switches = {
      IsBusinessPartner: searchParams.IsBusinessPartner,
      HierarchyType: searchParams.HierarchyType,
      ISR: searchParams.ISR,
      IsLegal: IsLegal,
      IsPFPartner: searchParams.IsPFPartner,
    }

    Object.keys(searchParams).forEach((field) => {
      if (searchParams[field]) {
        if (field === 'PartnerID') {
          filter.filters.push(
            partnerIds.length > 1
              ? {
                  logic: Logic.OR,
                  filters: partnerIds.map((id) => ({
                    field: 'PartnerID',
                    operator: Operator.CONTAINS,
                    value: id,
                  })),
                }
              : {
                  field: 'PartnerID',
                  operator: Operator.CONTAINS,
                  value: partnerIds[0] ?? '',
                }
          )
          return
        }
        switch (field) {
          case 'LearnerID':
          case 'ContactID':
          case 'IsDis':
          case 'LocationID':
          case 'STORE_NUM':
          case 'IsBusinessPartner':
            filter.filters.push({
              field: field,
              operator: Operator.EQUAL,
              value: searchParams[field],
            })
            break
          case 'PartnerID':
          case 'ParentPartnerID':
          case 'TaxID':
          case 'CompanyLegalName':
          case 'CommunicationName':
          case 'Country':
          case 'URL':
          case 'State':
          case 'FirstName':
          case 'LastName':
          case 'Email':
          case 'AgreementNo':
            filter.filters.push({
              field,
              operator: Operator.CONTAINS,
              value: searchParams[field],
            })
            break
          case 'ISR':
            if (
              appGetState().user.roleIds &&
              appGetState().user.roleIds.indexOf('PBM') === -1 &&
              !(field === 'ISR' && ~appGetState().user.roleIds.indexOf('AOM'))
            ) {
              filter.filters.push({
                field,
                operator: Operator.CONTAINS,
                value: searchParams[field],
              })
            }
            break
        }
        if (switches.IsLegal === 'legal' && type === SearchRequestType.Partner) {
          switch (field) {
            case 'City':
              AddressFilter.filters.push({
                field: 'LegalCity',
                operator: Operator.CONTAINS,
                value: searchParams[field],
              })
              break
            case 'Country':
              AddressFilter.filters.push({
                field: 'LegalCountry',
                operator: Operator.CONTAINS,
                value: searchParams[field],
              })
              break
            case 'ZIPCode':
              AddressFilter.filters.push({
                field: 'LegalPostalCode',
                operator: Operator.CONTAINS,
                value: searchParams[field],
              })
              break
            case 'AddressLine1':
              AddressFilter.filters.push({
                field: 'LegalAddr1',
                operator: Operator.CONTAINS,
                value: searchParams[field],
              })
              break
            case 'AddressLine2':
              AddressFilter.filters.push({
                field: 'LegalAddr2',
                operator: Operator.CONTAINS,
                value: searchParams[field],
              })
              break
            case 'AddressLine3':
              AddressFilter.filters.push({
                field: 'LegalAddr3',
                operator: Operator.CONTAINS,
                value: searchParams[field],
              })
              break
          }
        } else {
          switch (field) {
            case 'City':
            case 'ZIPCode':
            case 'AddressLine1':
            case 'AddressLine2':
            case 'AddressLine3':
              AddressFilter.filters.push({
                field,
                operator: Operator.CONTAINS,
                value: searchParams[field],
              })
              break
          }
        }
      }
    })

    if (type === SearchRequestType.Partner) {
      if (formData.HierarchyType) {
        filter.filters[filter.filters.length] = {
          field: 'HierarchyType',
          operator: Operator.EQUAL,
          value: formData.HierarchyType || '',
        }
      }
    }

    if (AddressFilter.filters.length) {
      filter.filters.push(AddressFilter)
    }

    if (query) {
      const simpleFilter = this.createFilter(query.trim(), switches, type)
      Object.keys(formData).forEach((key) => {
        simpleFilter.filters = simpleFilter.filters.filter(
          (i: any) => !formData[key] || i.field !== key
        )
      })
      filter.filters = [...filter.filters, simpleFilter] as any
    }
    return filter
  }

  public advancedSearchForFetcher = async (
    filter: any,
    type: SearchRequestType,
    paging: any
  ): Promise<any> => {
    const url = proxy(`${this.getPathByType(type)}`)
    const { app } = appGetState()
    const [accessToken] = await getTokens(app.ownerId)
    const headers = new HttpHeaders()
    if (accessToken) {
      headers.add('Authorization', `Bearer ${accessToken}`)
    }
    const pageSize = paging.pageSize
    const page = paging.page

    const payload = {
      take: pageSize,
      skip: page * pageSize,
    }
    const indexBPPOnly = filter.filters.findIndex(
      (el: any) => el.field === 'IsPFPartner' && el.value === '1'
    )
    const indexIsDis = filter.filters.findIndex(
      (el: any) => type === SearchRequestType.Partner && el.field === 'IsDis' && el.value === '1'
    )
    const IsAmpRet = filter.filters.findIndex(
      (el: any) => el.field === 'IsAmpRet' && el.value === '1'
    )
    const IsRet = filter.filters.findIndex((el: any) => el.field === 'IsRet' && el.value === '1')
    const IsAmpComm = filter.filters.findIndex(
      (el: any) => el.field === 'IsAmpComm' && el.value === '1'
    )
    const IsComm = filter.filters.findIndex((el: any) => el.field === 'IsComm' && el.value === '1')
    const IsPoly = filter.filters.findIndex((el: any) => el.field === 'IsPoly' && el.value === '1')
    const AgreementNo = filter.filters.findIndex(
      (el: any) => el.field === 'AgreementNo' && !!el.value
    )

    const Region = filter.filters.findIndex((el: any) => el.field === 'Region')

    if (~Region) {
      const regionFilter = filter.filters[Region]
      payload[`p.Region`] = regionFilter.value
      filter.filters.splice(Region, 1)
    } else {
      payload[`p.Region`] = this.region
    }

    if (~indexBPPOnly) {
      filter.filters.splice(indexBPPOnly, 1)
      payload[`p.IsBPPOnly`] = 1
    }
    if (~indexIsDis) {
      filter.filters.splice(indexIsDis, 1)
      payload[`p.IsDis`] = 1
    }
    if (~IsAmpRet) {
      filter.filters.splice(IsAmpRet, 1)
      payload[`p.IsAmpRet`] = 1
    }
    if (~IsAmpComm) {
      filter.filters.splice(IsAmpComm, 1)
      payload[`p.IsAmpComm`] = 1
    }
    if (~IsRet) {
      filter.filters.splice(IsRet, 1)
      payload[`p.IsRet`] = 1
    }
    if (~IsComm) {
      filter.filters.splice(IsComm, 1)
      payload[`p.IsComm`] = 1
    }
    if (~IsPoly) {
      filter.filters.splice(IsPoly, 1)
      payload[`p.IsPoly`] = 1
    }
    if (~AgreementNo) {
      payload[`p.AgreementNo`] = filter.filters[AgreementNo].value

      filter.filters.splice(AgreementNo, 1)
    }
    payload['p.Filter'] = serializeSearchFilter(filter)
    const response = await Rest.post({ url, payload, cancelObject: new HttpCancel(), headers })
    if (response) {
      return response['Rows']
    }

    return []
  }
}
